// The serializers whose only per-context difference is the article wrapper. Anything that varies
// beyond that gets its own file in this folder.
//
// A `className` here is not styling — it is the block announcing itself to the article layout,
// which selects on it as a *grid sibling* (`pages/Article/Styles.tsx`). So it has to sit on the
// wrapper, not inside the block. Only add one a selector actually reads.
import { createContextualSerializer, createStandaloneSerializer } from '../../createContextualSerializer';
import { AccordionListBlock } from '../components/AccordionList/AccordionList';
import { AccordionBlock } from '../components/Accordion/Accordion';
import { ActiveChargingTagsBlock } from '../components/ActiveChargingTags/ActiveChargingTags';
import { AnchorBlock } from '../components/Anchor/Anchor';
import { CarLoanCalculatorBlock } from '../components/CarLoanCalculator/CarLoanCalculator';
import { CodeTypesBlock } from '../components/CodeTypes/CodeTypes';
import { CompetitionReferenceBlock } from '../components/Competition/CompetitionBlock';
import { ConsumerTestProductList } from '../components/ConsumerTestProductList/ConsumerTestProductList';
import { CtaButtonBody } from '../components/CtaButtonBody/CtaButtonBody';
import { CtaButtonInternalLink } from '../components/CtaButtonInternalLink/CtaButtonInternalLink';
import { CtaButtonLink } from '../components/CtaButtonLink/CtaButtonLink';
import { CtaFileLinkBlock } from '../components/CtaFileLink/CtaFileLink';
import { CtaGlobalBlock } from '../components/CtaGlobalBlock/CtaGlobalBlock';
import { DataWrapperBlock } from '../components/DataWrapper/DataWrapperBlock';
import { DocumentsBlock } from '../components/DocumentsBlock/DocumentsBlock';
import { DynamicFormSelectorBlock } from '../components/DynamicFormSelectorSerializer/DynamicFormSelectorSerializer';
import { ExternalLink } from '../components/ExternalLink/ExternalLink';
import { FAQGlobalReference } from '../components/FAQGlobalReference/FAQGlobalReference';
import { FormBlock } from '../components/Form/FormBlock';
import { HighChartsBlock } from '../components/HighCharts/HighCharts';
import { IdpCountriesListBlock } from '../components/IdpCountriesList/IdpCountriesList';
import { IframeBlock } from '../components/Iframe/Iframe';
import * as IframeStyles from '../components/Iframe/Iframe.styles';
import { ImageCarouselBlock } from '../components/ImageCarouselSerializer/ImageCarouselSerializer';
import { ImagesBlock } from '../components/Images/Images';
import { InfoBoxBlock } from '../components/InfoBox/InfoBox';
import { InsuranceCalculatorBlock } from '../components/InsuranceCalculator/InsuranceCalculator';
import { InternalLinkBlock } from '../components/InternalLink/InternalLink';
import { ModularButtonBlock } from '../components/ModularButtonSerializer/ModularButtonSerializer';
import { NumberOfUsedRoadAssistanceBlock } from '../components/NumberOfRoadsideAssistance/NumberOfRoadsideAssistance';
import { NumberedList } from '../components/NumberedListSerializer/NumberedListSerializer';
import { OilChangeCalculatorBlock } from '../components/OilChangeCalculator/OilChangeCalculator';
import { ParallaxBlock } from '../components/Parallax/Parallax';
import * as ParallaxStyles from '../components/Parallax/Parallax.styles';
import { PayWallBlock } from '../components/PayWall/PayWall';
import { PodcastBlock } from '../components/Podcast/Podcast';
import { PollReferenceBlock } from '../components/Poll/PollBlock';
import { PortraitsWithText } from '../components/PortraitsWithText/PortraitsWithText';
import { ProsAndConsBlock } from '../components/ProsAndConsSerializer/ProsAndConsSerializer';
import { QuizBlock } from '../components/Quiz/Quiz';
import { QuoteBlock } from '../components/Quote/Quote';
import { ShortsCarouselBlock } from '../components/ShortsCarouselSerializer/ShortsCarouselSerializer';
import { SocialProofBlock } from '../components/SocialProof/SocialProof';
import { StepByStepListBlock } from '../components/StepByStepListSerializer/StepByStepListSerializer';
import { TableBlock } from '../components/Table/Table';
import { TestCollectionBlock } from '../components/TestCollection/TestCollection';
import { Block } from '../components/TestResultSerializer/TestResultSerializer';
import { WindShieldWiperChangeCalculatorBlock } from '../components/WiperChangeCalculator/WiperChangeCalculator';

export const AccordionListBlockSerializer = createContextualSerializer(AccordionListBlock);
export const AccordionSerializer = createContextualSerializer(AccordionBlock, { className: 'blockAccordion' });
export const ActiveChargingTagsSerializer = createStandaloneSerializer(ActiveChargingTagsBlock);
export const AnchorSerializer = createContextualSerializer(AnchorBlock);
export const CarLoanCalculatorSerializer = createContextualSerializer(CarLoanCalculatorBlock);
export const CodeTypesSerializer = createContextualSerializer(CodeTypesBlock);
export const CompetitionSerializer = createContextualSerializer(CompetitionReferenceBlock);
export const ConsumerTestProductListSerializer = createContextualSerializer(ConsumerTestProductList);
export const CtaButtonBodySerializer = createContextualSerializer(CtaButtonBody);
export const CtaButtonInternalLinkSerializer = createContextualSerializer(CtaButtonInternalLink, {
  className: 'CtaButton',
});
export const CtaButtonLinkSerializer = createContextualSerializer(CtaButtonLink, { className: 'CtaButton' });
export const CtaFileLinkSerializer = createContextualSerializer(CtaFileLinkBlock);
export const CtaGlobalBlockSerializer = createContextualSerializer(CtaGlobalBlock);
export const DataWrapperSerializer = createContextualSerializer(DataWrapperBlock);
export const DocumentsBlockSerializer = createContextualSerializer(DocumentsBlock);
export const DynamicFormSelectorSerializer = createContextualSerializer(DynamicFormSelectorBlock);
export const ExternalLinkSerializer = createContextualSerializer(ExternalLink, { className: 'externalLink' });
export const FAQGlobalReferenceSerializer = createContextualSerializer(FAQGlobalReference);
export const FormSerializer = createContextualSerializer(FormBlock);
export const HighChartsSerializer = createContextualSerializer(HighChartsBlock);
export const IdpCountriesListSerializer = createStandaloneSerializer(IdpCountriesListBlock);
export const IframeSerializer = createContextualSerializer(IframeBlock, {
  wrapper: IframeStyles.StyledArticleBlockContentGridCol,
});
export const ImageCarouselSerializer = createContextualSerializer(ImageCarouselBlock);
export const ImagesSerializer = createContextualSerializer(ImagesBlock);
export const InfoBoxSerializer = createContextualSerializer(InfoBoxBlock);
export const InsuranceCalculatorSerializer = createStandaloneSerializer(InsuranceCalculatorBlock);
export const InternalLinkSerializer = createContextualSerializer(InternalLinkBlock, { className: 'internalLink' });
export const ModularButtonBlockSerializer = createContextualSerializer(ModularButtonBlock);
export const NumberOfUsedRoadAssistanceSerializer = createStandaloneSerializer(NumberOfUsedRoadAssistanceBlock);
export const NumberedListSerializer = createContextualSerializer(NumberedList);
export const OilChangeCalculatorSerializer = createStandaloneSerializer(OilChangeCalculatorBlock);
export const ParallaxSerializer = createContextualSerializer(ParallaxBlock, {
  wrapper: ParallaxStyles.ArticleParallaxContentGridCol,
});
export const PayWallSerializer = createContextualSerializer(PayWallBlock);
export const PodcastSerializer = createContextualSerializer(PodcastBlock);
export const PollSerializer = createContextualSerializer(PollReferenceBlock);
export const PortraitsWithTextSerializer = createContextualSerializer(PortraitsWithText);
export const ProsAndConsSerializer = createContextualSerializer(ProsAndConsBlock);
export const QuizSerializer = createContextualSerializer(QuizBlock);
export const QuoteSerializer = createContextualSerializer(QuoteBlock, { className: 'quote' });
export const ShortsCarouselSerializer = createContextualSerializer(ShortsCarouselBlock);
export const SocialProofSerializer = createContextualSerializer(SocialProofBlock);
export const StepByStepListSerializer = createContextualSerializer(StepByStepListBlock);
export const TableSerializer = createContextualSerializer(TableBlock);
export const TestCollectionSerializer = createContextualSerializer(TestCollectionBlock);
export const TestResultSerializer = createContextualSerializer(Block);
export const WindShieldWiperChangeCalculatorSerializer = createStandaloneSerializer(
  WindShieldWiperChangeCalculatorBlock,
);
